import React, { useMemo } from "react";

import {
    SearchState,
    SearchDispatch
} from "../interfaces/context/searchContextInterfaces";
import { locationReducer } from "../reducers/locationReducer";
import { companyReducer } from "../reducers/companyReducer";
import { keywordReducer } from "../reducers/keywordReducer";
import { filtersReducer } from "../reducers/filtersReducer";
import { userInteractionsReducer } from "../reducers/userInteractionsReducer";
import { urlReducer } from "../reducers/urlReducer";
import { searchIdReducer } from "../reducers/searchIdReducer";
import { useCombinedReducers } from "../utils/reducers";

export const defaultSearchState: SearchState = {
    location: {
        userInputValue: "",
        userSelectedLocation: {
            label: "",
            latitude: null,
            longitude: null,
            value: "",
            remote_work_location_type: null,
        },
        locationSuggestions: []
    },
    company: {
        userInputValue: "",
        userSelectedCompany: null,
        companySuggestions: []
    },
    keyword: {
        userInputValue: "",
        userSelectedKeyword: null
    },
    filters: {
        category: [],
        industry: [],
        experienceLevel: [],
        perks: [],
        companySize: [],
        companyLeadership: []
    },
    userInteractions: {
        tilesViewedJobIds: []
    },
    url: "",
    preference: "",
    searchId: "",
    ctsEnabled: false
};

const SearchStateContext = React.createContext<SearchState | undefined>(defaultSearchState);
const SearchDispatchContext = React.createContext<SearchDispatch | undefined>(undefined);
/**
 * Search  Context Provider
 *
 * How to use:
 *
 * Wrap the provider around the components that need access to the context data.
 * ```
 * <SearchProvider>
 *   <Component />
 * </SearchProvider>
 * ```
 * Access the state in <Component />
 * ```
 * const state = useSearchState();
 * ```
 * Call dispatch to update the context state
 * ```
 * const dispatch = useSearchDispatch();
 * dispatch({ type: ACTION_TYPE, payload: ACTION_PAYLOAD });
 * ```
 */
const SearchProvider: React.FC<React.PropsWithChildren<SearchState>> = (props) => {
    const {
        children, location, company, keyword, filters, userInteractions, preference, searchId, ctsEnabled
    } = props;
    const [state, dispatch] = useCombinedReducers({
        location: React.useReducer(locationReducer, {
            ...defaultSearchState.location,
            ...location
        }),
        company: React.useReducer(companyReducer, {
            ...defaultSearchState.company,
            ...company
        }),
        keyword: React.useReducer(keywordReducer, {
            ...defaultSearchState.keyword,
            ...keyword
        }),
        filters: React.useReducer(filtersReducer, {
            ...defaultSearchState.filters,
            ...filters
        }),
        userInteractions: React.useReducer(userInteractionsReducer, {
            ...defaultSearchState.userInteractions,
            ...userInteractions
        }),
        url: React.useReducer(urlReducer, defaultSearchState.url),
        searchId: React.useReducer(searchIdReducer, searchId)
    });

    const searchState = useMemo(() => ({
        ...state,
        preference: preference ?? defaultSearchState.preference,
        ctsEnabled: ctsEnabled === true
    }), [state, preference, ctsEnabled]);

    return (
        <SearchStateContext.Provider value={searchState as SearchState}>
            <SearchDispatchContext.Provider value={dispatch as SearchDispatch}>
                {children}
            </SearchDispatchContext.Provider>
        </SearchStateContext.Provider>
    );
};

const useSearchState = () => {
    const context = React.useContext(SearchStateContext);
    if (context === undefined) {
        throw new Error("useSearchState must be used within the SearchProvider");
    }
    return context;
};

const useSearchDispatch = () => {
    const context = React.useContext(SearchDispatchContext);
    if (context === undefined) {
        throw new Error("useSearchDispatch must be used within the SearchProvider");
    }
    return context;
};

SearchProvider.displayName = "SearchProvider";
useSearchState.displayName = "useSearchState";
useSearchDispatch.displayName = "useSearchDispatch";

export { SearchProvider, useSearchState, useSearchDispatch };
